import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Link,
  TextField,
  MenuItem,
  Grid,
  useTheme,
  FormHelperText,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { parseAmount } from "./listNewAssetDraft";
import { filledFieldWrapSx } from "./listNewAssetFilledSurface";

const BLOCKCHAIN_OPTIONS = [
  {
    value: "ethereum",
    label: "Ethereum",
    helper: "Ethereum mainnet offers broad ecosystem support.",
  },
];

const labelSx = { fontWeight: 600, fontSize: 14, color: "text.primary", mb: 0.75 };

function sanitizeNumeric(v) {
  const s = v.replace(/[^\d.]/g, "");
  const i = s.indexOf(".");
  return i === -1 ? s : s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
}

export default function ListNewAssetTokenConfiguration({ draft,
  onDraftChange,
  nextStep,
  prevStep,
  wizardStep = 7,
  wizardTotalSteps = 8,
  categoryChipLabel = "Real Estate", }) {
  const theme = useTheme();
  const [errors, setErrors] = useState({});
  const TEAL = theme.palette.brand.cyan;
  const TEAL_HOVER = theme.palette.brand.cyanDark;

  useEffect(() => {
    if (draft.blockchainNetwork !== "ethereum") {
      onDraftChange({ blockchainNetwork: "ethereum" });
    }
  }, [draft.blockchainNetwork, onDraftChange]);

  const network = "ethereum";
  const networkMeta = BLOCKCHAIN_OPTIONS[0];

  /** Align with Step 5: blank price defaults to 10 SAR for display math */
  const defaultMinFromPrice = useMemo(() => {
    const p = parseAmount(draft.pricePerTokenSar ?? "");
    const effectivePrice = p > 0 ? p : 10;
    return new Intl.NumberFormat("en-US").format(2 * effectivePrice);
  }, [draft.pricePerTokenSar]);

  const minInvestment =
    draft.minimumInvestmentSar != null && String(draft.minimumInvestmentSar).trim() !== ""
      ? String(draft.minimumInvestmentSar)
      : defaultMinFromPrice;

  const minSar = useMemo(() => parseAmount(minInvestment), [minInvestment]);

  const { totalSupply, pricePerTokenSar, totalValuationSar } = useMemo(() => {
    const valuation = parseAmount(draft.totalAssetValue ?? "");
    const explicitPrice = parseAmount(draft.pricePerTokenSar ?? "");
    const price = explicitPrice > 0 ? explicitPrice : 10;
    const supply =
      valuation > 0 && price > 0 ? Math.max(0, Math.floor(valuation / price)) : 0;
    return {
      totalSupply: supply,
      pricePerTokenSar: price,
      totalValuationSar: valuation > 0 ? valuation : 0,
    };
  }, [draft.totalAssetValue, draft.pricePerTokenSar]);

  const formatSar = (n) =>
    new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(n) + " SAR";

  const handleNext = () => {
    if (minSar <= 0) {
      setErrors({ minimumInvestmentSar: "Please enter a valid minimum investment (SAR)." });
      return;
    }
    setErrors({});
    nextStep();
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1100,
        mx: "auto",
        minHeight: "100vh",
      }}
    >

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={prevStep}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: TEAL,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
            "&:hover": { color: TEAL_HOVER },
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
          Back
        </Link>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={categoryChipLabel}
            size="small"
            sx={{
              bgcolor: (theme) => theme.palette.brand.cyanSoft,
              color: (theme) => theme.palette.brand.cyanDark,
              fontWeight: 600,
              fontSize: 12,
              height: 26,
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.muted" }}>
            Step {wizardStep} of {wizardTotalSteps}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
        {Array.from({ length: wizardTotalSteps }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 999,
              bgcolor: i < wizardStep ? TEAL : theme.palette.border.light,
            }}
          />
        ))}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          borderRadius: "14px",
          bgcolor: "background.paper",
          boxShadow: (theme) => theme.palette.shadow.cardStrong,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", mb: 0.75 }}>
          Token Configuration
        </Typography>
        <Typography variant="body2" sx={{ color: "text.muted", mb: 3 }}>
          Configure how your asset will be tokenized
        </Typography>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={filledFieldWrapSx(theme, Boolean(network))}>
              <Typography component="label" sx={labelSx}>
                Blockchain Network
              </Typography>
              <TextField
                select
                variant="standard"
                fullWidth
                value={network}
                onChange={(e) => onDraftChange({ blockchainNetwork: e.target.value })}
                slotProps={{ input: { disableUnderline: true } }}
                sx={{
                  mt: 1,
                  "& .MuiSelect-select": {
                    px: 2,
                    py: 1.25,
                    fontSize: 14,
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                  },
                  "& .MuiSelect-icon": {
                    color: theme.palette.text.faint,
                  },
                }}
              >
                {BLOCKCHAIN_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <Typography sx={{ fontSize: 13, color: "text.muted", mt: 1, lineHeight: 1.5 }}>
                {networkMeta.helper}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={filledFieldWrapSx(theme, minSar > 0)}>
              <Typography component="label" sx={labelSx}>
                Minimum Investment (SAR)
              </Typography>
              <TextField
                variant="standard"
                fullWidth
                placeholder={defaultMinFromPrice}
                value={minInvestment}
                onChange={(e) => { onDraftChange({ minimumInvestmentSar: sanitizeNumeric(e.target.value) }); setErrors((p) => ({ ...p, minimumInvestmentSar: "" })); }}
                slotProps={{ input: { disableUnderline: true } }}
                sx={{
                  mt: 1,
                  "& .MuiInputBase-input": {
                    px: 2,
                    py: 1.25,
                    fontSize: 14,
                  },
                }}
              />
            </Box>
            {errors.minimumInvestmentSar && (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.minimumInvestmentSar}</FormHelperText>
            )}
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "14px",
            border: (theme) => `1px solid ${theme.palette.brand.cyan}`,
            bgcolor: (theme) => theme.palette.brand.cyanSoft,
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.accent.info.text, fontWeight: 600, mb: 2 }}>
            Token Economics Summary
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: "14px",
                  border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
                  bgcolor: "background.paper",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: "brand.cyanDark", mb: 1 }}>
                  Total Token Supply
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
                  {new Intl.NumberFormat("en-SA").format(totalSupply)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: "14px",
                  border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
                  bgcolor: "background.paper",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: "brand.cyanDark", mb: 1 }}>
                  Price Per Token
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
                  {formatSar(pricePerTokenSar)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: "14px",
                  border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
                  bgcolor: "background.paper",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: "brand.cyanDark", mb: 1 }}>
                  Total Valuation
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
                  {formatSar(totalValuationSar)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* <FormControlLabel
          control={
            <Checkbox
              checked={fractional}
              onChange={(e) => onDraftChange({ fractionalOwnership: e.target.checked })}
              sx={{
                color: (theme) => theme.palette.text.faint,
                "&.Mui-checked": { color: TEAL },
              }}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: "text.primary" }}>
                Enable Fractional Ownership
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.muted", mt: 0.25 }}>
                Allow investors to purchase fractional parts of a token if the price is high.
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
        /> */}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 4, pt: 2 }}
        >
          <Button
            variant="outlined"
            onClick={prevStep}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: (theme) => theme.palette.border.medium,
              color: "text.neutral",
              px: 3,
              py: 1,
              "&:hover": {
                borderColor: (theme) => theme.palette.text.faint,
                bgcolor: "background.subtle",
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={handleNext}
            sx={{
              px: 1.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: TEAL,
              borderRadius: "12px",
              boxShadow: "none",
              "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
            }}
          >
            Next Step
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
